import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react'
import Entry from './Entry.jsx'
import CellEditor from './CellEditor.jsx'
import Icon from './Icon.jsx'
import { OffBadge, OffPartialTile } from './OffMarker.jsx'
import { useAuth } from '../contexts/AuthContext.jsx'
import ContextMenu, { ContextMenuItem, ContextMenuLabel, ContextMenuSeparator } from './ContextMenu.jsx'
import { formatShort, startOfWeek, weekdayName, isPartialOn } from '../lib/dates.js'
import { isBirthday } from '../lib/birthday.js'
import { tintOf } from '../lib/theme.js'
import { useGridKeys, focusCell } from '../lib/useGridKeys.js'

/** The group key used everywhere a group needs to be a map/set key or compared for equality —
 * "Unassigned staff" has a real id of null, which can't be used as one directly. */
const groupKey = (group) => group?.id ?? 'unassigned'

/**
 * The modifier that turns a drag into a copy.
 *
 * Ctrl on Windows and Linux, Command on a Mac — where Ctrl is not the copy modifier, and
 * where ctrlKey alone meant a Mac could not copy-drag at all. Read off the drag event rather
 * than remembered from a keydown, because the key can be pressed or released mid-drag and
 * the only question is whether it is held right now.
 */
const isCopyDrag = (e) => e.ctrlKey || e.metaKey

/**
 * Grouped by person, a board has one band holding everybody and the crew lives on each job.
 * These two answer "is that the shape we are in" and "what colour is this job", and both
 * grids ask them the same way.
 */
const isPeopleBoard = (board) => board?.groups?.length === 1 && board.groups[0].kind === 'people'
const crewStyleFor = (entry, crewsById) => {
  const colour = crewsById?.get(entry?.crewGroupId)?.color
  return colour ? { '--crew': colour } : undefined
}

/**
 * Whether this cell would actually take the drop — the same test for the highlight and for
 * the drop itself, so a cell never lights up and then refuses.
 *
 * Dropping an entry back onto its own cell is excluded: it would PATCH a row with the values
 * it already has, spending a request, an audit entry and a version bump to change nothing.
 */
function canDropHere(dragging, e, editable, cellEntry) {
  if (!dragging || !editable || !isCopyDrag(e)) return false
  if (cellEntry && cellEntry.id === dragging.id) return false
  return true
}

/**
 * Runs of consecutive days holding the same single job, for one row of the week grid.
 *
 * Somebody on the same job all week is nine words repeated across nine cells, and the thing
 * worth seeing — that it is ONE stretch of work — is the thing the grid lines hide. So a run
 * is drawn as one bar and says its name once.
 *
 * The whole row opts out the moment any one of its days holds more than one job. A cell with
 * two jobs cannot be half of a bar, and deciding per-run instead would produce a row that is
 * merged at one end and not the other — which reads as a rendering fault rather than as a
 * distinction. Wherever the busy day falls, start, middle or end, the row is drawn plainly.
 *
 * Identity is the whole job, not just its name: same label, same job type, same crew. Two
 * days that differ in something invisible here must not be merged into a claim they are the
 * same, and the crew is what colours the bar.
 *
 * @returns Map<dateIso, { key, first, last }> — only for dates inside a run of 2 or more.
 */
function sameJobRuns(days, entriesOn, rowKey) {
  const cells = days.map((d) => entriesOn(d.date))
  if (cells.some((es) => es.length > 1)) return null

  const idOf = (es) => {
    if (es.length !== 1) return null
    const e = es[0]
    return [e.label, e.jobTypeId || '', e.crewGroupId ?? e.catTeamId ?? ''].join('|~|')
  }

  const runs = new Map()
  let i = 0
  while (i < days.length) {
    const id = idOf(cells[i])
    if (!id) { i += 1; continue }
    let j = i + 1
    while (j < days.length && idOf(cells[j]) === id) j += 1
    if (j - i >= 2) {
      const key = `${rowKey}:${days[i].date}`
      for (let k = i; k < j; k++) {
        runs.set(days[k].date, { key, first: k === i, last: k === j - 1, len: j - i })
      }
    }
    i = j
  }
  return runs.size ? runs : null
}

/**
 * The week grid's version. A cell there holds a day's worth of entries rather than one, so
 * whether the drop lands on an existing entry is only known at drop time — the cell as a
 * whole is droppable, and the cell being blocked — away, or out with another crew — is what
 * rules it out.
 */
function weekCanDrop(dragging, e, blocked, canCreateHere) {
  return Boolean(dragging) && isCopyDrag(e) && !blocked && Boolean(canCreateHere)
}

/**
 * The swatch and the crew's name, shared by the day and week header rows.
 *
 * No position number in front of it. The crews are already in a deliberate order down the
 * page and the colour swatch already tells them apart — the number was a third thing saying
 * what those two say, and it read as a rank the crews do not have.
 *
 * A CAT crew's subtitle is its coordinator, and that is not shown here either: on the board
 * it is the same name on every row of the band, which is a lot of width spent on something
 * nobody is looking up mid-shift. The wall still carries it, where a crew's lead IS what
 * somebody walking past needs, and Settings has it whenever it needs looking up. A PM
 * group's subtitle stays: "PMA: …" is not the same fact, and normal mode has no numbering
 * to have prompted any of this.
 */
function GroupHeaderContent({ group }) {
  return (
    <>
      {group.color && <span className="group-swatch" style={{ background: group.color }} />}
      {group.name}
      {group.kind !== 'cat' && group.subtitle && (
        <span className="group-sub">{group.subtitle}</span>
      )}
    </>
  )
}

function GroupHeader({ group, colSpan, splitIndex = -1, collapsed = false, onToggle = null, count = 0 }) {
  // The same wash the day grid gives its crew bands — a shade stronger on the header than on
  // the rows beneath it. The week grid tinted its staff rows but never its headings, so nine
  // coloured blocks were each introduced by a grey one; the swatch was the only thing tying
  // a heading to the band under it.
  const headTint = group.color ? tintOf(group.color, 0.8) : null
  // The fortnight rule has to cross the crew bands too, and a single full-width cell gives
  // it no column edge to sit on. So the band is two cells on that view — everything up to
  // the boundary, then the second week — which puts a real border where the rule goes and
  // leaves the band looking unchanged. One cell still, on any view with no split.
  const split = splitIndex > 0
  return (
    <tr
      className={`group-row ${onToggle ? 'clickable' : ''}`}
      style={headTint ? { '--team-tint': headTint } : undefined}
      onClick={onToggle || undefined}
    >
      <td colSpan={split ? splitIndex + 1 : colSpan}>
        {/* The team name is pinned to the left of its own row rather than left where the row
            starts. This cell spans every column, so on the fortnight view — the one that
            scrolls sideways — its text sat two thousand pixels off screen the moment you
            scrolled, leaving a row of blank colour bands with nothing to say which crew each
            one was. Sticky on the CONTENT, because a colSpan cell cannot usefully stick
            itself: it is already as wide as the table. */}
        <span className="group-row-name">
          {onToggle ? (
            <span className="group-head">
              <button
                className={`collapse-btn ${collapsed ? 'collapsed' : ''}`}
                aria-label={collapsed ? `Show ${group.name}` : `Collapse ${group.name}`}
                aria-expanded={!collapsed}
                onClick={(e) => { e.stopPropagation(); onToggle() }}
              >
                <Icon name="down" size={14} strokeWidth={2} />
              </button>
              <GroupHeaderContent group={group} />
              {/* Folded, the heading has to say how much is behind it — otherwise a shut band
                  and an empty one look the same. */}
              {collapsed && count > 0 && <span className="group-count">{count}</span>}
            </span>
          ) : (
            <GroupHeaderContent group={group} />
          )}
        </span>
      </td>
      {split && <td className="week-split" colSpan={colSpan - splitIndex - 1} />}
    </tr>
  )
}

/**
 * DAY VIEW — rows are people, columns are the job slots for that one day. This is the
 * editing surface: it is the shape a construction whiteboard already has.
 *
 * In CAT mode, whoever can manage CAT team structure (an owner, or an admin the tenant has
 * delegated to) can additionally drag a row onto a different team, or cut a row and paste it
 * onto a team elsewhere — both are just two ways to reach the same action, reassigning who
 * is on which crew. Nothing here writes an assignment; it writes cat_team_members, through
 * the very same endpoint the CAT-setup crew picker already uses.
 */
export function DayGrid({
  board,
  crews = null,
  date,
  editing,
  setEditing,
  canCreateIn,
  canEdit,
  onSave,
  onDelete,
  onAddColumn,
  readOnly = false,
  canManageTeams = false,
  cut = null,
  onCutStaff,
  onMoveStaff,
  collapsedGroups,
  onToggleCollapse,
  pins = { left: [], right: [] }, // group keys per side, in stack order — a team sits in at most one
  onSetPins,
  sideWidths = { left: 380, right: 380 },
  onSetSideWidths,
  clip = null, // { label, jobTypeId, catTeamId } | null — a copied cell, for paste elsewhere
  onCopyEntry,
  // Opens "Copy this day to…" for a person and a date. Null where nothing may be written.
  onCopyDay = null,
  onAddTimeOff,
  // Opening an absence that is already there, from the marker that draws it. Null for a
  // reader: the marker then renders as text rather than a control nothing will answer.
  onEditTimeOff = null,
  // The cell the notes drawer has just pointed at. Its own key rather than `editing`: that
  // one means "an editor is open over this cell", and borrowing it would both open the editor
  // and leave the cell refusing its own clicks afterwards.
  revealedKey = null,
}) {
  const day = board.days.find((d) => d.date === date) || board.days[0]
  const slotCount = day?.slotCount || 3
  const slots = Array.from({ length: slotCount }, (_, i) => i + 1)

  const jobTypes = new Map(board.jobTypes.map((j) => [j.id, j]))
  const groupsById = new Map(board.groups.map((g) => [g.id, g]))
  const staffById = new Map(board.staff.map((s) => [s.id, s]))

  /**
   * Moving somebody between crews — the grip, the drag, Cut row and Paste row.
   *
   * Off entirely when the board is grouped by person. There is one band there, "Everyone", so
   * there is nowhere to move a row TO: Cut offered an action whose only possible destination
   * was the band it came from, and the drag handle promised the same. The crew a person is on
   * is still changed the way it always was, from the board grouped by crew.
   */
  const canReorg =
    board.mode === 'cat' && !readOnly && canManageTeams && !isPeopleBoard(board)
  // Collapsing and side-pinning are both local view state, destructive of nothing, so they
  // are open to anyone working the interactive board — not just team managers.
  const canCollapse = board.mode === 'cat' && !readOnly
  const canPin = board.mode === 'cat' && !readOnly && Boolean(onSetPins)

  // Index by the group the entry should be DRAWN under, which for a CAT borrow is the
  // borrowing team rather than the person's home team.
  const cellIndex = new Map()
  for (const a of board.assignments) {
    if (a.date !== date) continue
    cellIndex.set(`${a.renderGroupId || 'none'}:${a.staffId}:${a.slot}`, a)
  }

  // Which crew somebody is on TODAY. board.groups carries the standing roster — the template
  // a day starts from — and board.dayTeams carries only the dates that deviate from it, so a
  // move made on one day never has to be un-made on the others.
  const overrideToday = new Map()
  for (const o of board.dayTeams || []) {
    if (o.date === date) overrideToday.set(o.staffId, o.groupId)
  }

  const rowsFor = new Map(board.groups.map((g) => [g.id, [...g.staffIds]]))
  const homeGroupOfStaff = new Map()
  for (const g of board.groups) for (const sid of g.staffIds) homeGroupOfStaff.set(sid, g.id)
  // Apply the day's overrides: pull the person out of the crew the template puts them on,
  // and add them to the one this date says instead.
  for (const [staffId, groupId] of overrideToday) {
    for (const rows of rowsFor.values()) {
      const at = rows.indexOf(staffId)
      if (at !== -1) rows.splice(at, 1)
    }
    rowsFor.get(groupId)?.push(staffId)
  }
  const crewToday = (staffId) =>
    overrideToday.has(staffId) ? overrideToday.get(staffId) : homeGroupOfStaff.get(staffId) ?? null

  for (const a of board.assignments) {
    if (a.date !== date) continue
    const rows = rowsFor.get(a.renderGroupId)
    if (rows && !rows.includes(a.staffId)) rows.push(a.staffId)
  }

  // Away today. A range covering this date wins over anything booked in a cell: somebody
  // marked off is off, and a stale job left in a slot must not read as if they are coming.
  // Two kinds of away. The whole day takes the row out of play, as it always has. A window
  // within the day leaves the row bookable and says when the person is not here — the board
  // has no job times to check it against, so it is information for whoever is filling it,
  // not a rule. Where somebody has both on one date the whole day wins, since the window
  // adds nothing to it.
  const offToday = new Map()
  const offPartToday = new Map()
  for (const t of board.timeOff || []) {
    if (!(t.from <= date && date <= t.to)) continue
    if (isPartialOn(t, date)) {
      if (!offPartToday.has(t.staffId)) offPartToday.set(t.staffId, t)
    } else {
      offToday.set(t.staffId, t)
    }
  }

  // A CAT borrow also leaves a marker in the home team's block, so that coordinator sees
  // where their person went instead of an unexplained blank. Keyed by SLOT: someone can do
  // their own team's work at Job 1 and be lent out at Job 2, and collapsing that to a
  // whole-row banner hides the job they are actually doing for you.
  const borrowedOut = new Map()
  if (board.mode === 'cat') {
    for (const a of board.assignments) {
      if (a.date !== date || !a.borrowedFromGroupId) continue
      borrowedOut.set(`${a.borrowedFromGroupId}:${a.staffId}:${a.slot}`, a)
    }
  }

  // What's already booked THIS day, anywhere on the board — not a history of every label
  // ever typed, which only drifts further from relevant the longer a company uses this.
  // Newest first, since whatever just got typed on the next cell over is the most likely
  // thing about to get typed again.
  const dayLabels = []
  const seenLabels = new Set()
  for (let i = board.assignments.length - 1; i >= 0; i--) {
    const a = board.assignments[i]
    if (a.date !== date) continue
    const key = a.label.toLowerCase()
    if (seenLabels.has(key)) continue
    seenLabels.add(key)
    dayLabels.push(a.label)
  }

  // ── Drag-and-drop state — purely local. Which row is mid-drag, and which team is
  // currently under the pointer, so the drop target can be highlighted. The actual move is
  // one call up to the caller once a valid drop lands. ──────────────────────────────────
  const [dragging, setDragging] = useState(null) // { staffId, fromGroupId } | null
  const [dragOverGroupId, setDragOverGroupId] = useState(undefined) // undefined = none

  // A second, unrelated drag: any job cell is draggable, and dropping it on another cell
  // WHILE CTRL IS HELD copies it there — the OS convention for "drag normally to move, hold
  // the modifier to copy" (Explorer and Finder both do this), reused here for a job instead
  // of a file. Checked live on dragover/drop rather than only at dragstart, because that is
  // what makes it the OS gesture rather than a lookalike: you can decide to copy partway
  // through a drag you already started, the same as everywhere else that gesture exists.
  // Without Ctrl a drop here does nothing — there is no plain "move a job by dragging" here,
  // and this only exists to be the copy half of that convention.
  const [draggingEntry, setDraggingEntry] = useState(null) // { id, label, jobTypeId } | null
  // Which cell a copy-drag is currently over, so the board can say where it would land.
  // Cleared on drop and on drag end — an abandoned drag still fires dragend, so this cannot
  // be left lit.
  const [dropCell, setDropCell] = useState(null)
  // Both borrow markers — the "out with X" note left in the home crew and the "← from X"
  // chip on the borrowing crew's entry — answer to the one switch in Group setup, and are
  // off unless a tenant asks for them.
  const showBorrowMarkers = Boolean(board.settings?.showHomeTeamMarker)
  // Crews are not bands on a people board, so they come alongside it for colour and for
  // the editor's picker.
  const byPerson = isPeopleBoard(board)
  const crewsById = new Map((crews || []).map((c) => [c.id, c]))

  // ── Context menu — also local. It only ever triggers the same two callbacks the drag
  // handlers use, so the menu itself carries no move logic of its own. ───────────────────
  const [menu, setMenu] = useState(null) // { x, y, kind:'row'|'team', staffId?, groupId } | null

  // Where the open editor is anchored, and what it is editing. `editing` itself stays owned
  // by the caller so the board keeps a single idea of which cell is open.
  const [editCtx, setEditCtx] = useState(null)
  const openEditor = (key, rect, ctx) => {
    setEditCtx({ ...ctx, anchor: rect })
    setEditing(key)
  }
  const closeEditor = () => {
    setEditCtx(null)
    setEditing(null)
    // Back onto the cell the editor was over, so the arrow keys carry on from where the
    // person was rather than from the top of the document.
    focusCell(editing)
  }
  // Arrow keys between cells, Enter to open one. The same hook the week grid uses; off for
  // a read-only board, which gets no tab stop.
  const gridKeys = useGridKeys(!readOnly)
  // A successful save clears `editing` from the caller's side; drop the anchor with it so a
  // stale entry object is not held until the next cell is opened — and put focus back on the
  // cell, the same as a cancel does, so a save from the keyboard leaves the keyboard where it
  // was.
  const lastEditing = useRef(null)
  useEffect(() => {
    if (editing) lastEditing.current = editing
    else if (editCtx) {
      setEditCtx(null)
      focusCell(lastEditing.current)
    }
  }, [editing, editCtx])

  // Recording time off is not a CAT-team power — a PM marking somebody sick on a normal
  // board needs it just as much — so the row menu opens for it even where reorganising
  // crews is not on offer.
  const canTimeOff = !readOnly && Boolean(onAddTimeOff)

  const openRowMenu = (e, staffId, homeGroupId) => {
    if (!canReorg && !canTimeOff) return
    e.preventDefault()
    setMenu({ x: e.clientX, y: e.clientY, kind: 'row', staffId, groupId: homeGroupId })
  }
  // Pinning a team aside is useful to anyone reading the board, so the team menu opens for
  // them too — it just carries fewer items when they cannot reorganise.
  const openTeamMenu = (e, groupId) => {
    if (!canReorg && !canPin) return
    e.preventDefault()
    setMenu({ x: e.clientX, y: e.clientY, kind: 'team', groupId })
  }
  /**
   * A right-click on a job cell offers the booking actions (copy/paste/clear) on top of the
   * row actions, so the cut/paste-row pair stays reachable from anywhere on the row. On the
   * wall display it must not fire at all — that screen's own right-click menu lives on the
   * container and this would swallow the event before it got there.
   */
  const openCellMenu = (e, ctx) => {
    if (readOnly) return
    // canTimeOff joins the list for the same reason it does in the week grid: booking
    // somebody off needs no job in the cell and no right to reorganise crews.
    if (!ctx.entry && !ctx.editable && !canReorg && !canTimeOff) return
    e.preventDefault()
    e.stopPropagation()
    setMenu({ x: e.clientX, y: e.clientY, kind: 'cell', ...ctx })
  }

  const startDrag = (e, staffId, homeGroupId) => {
    if (!canReorg) return
    e.dataTransfer.setData('text/plain', staffId)
    e.dataTransfer.effectAllowed = 'move'
    setDragging({ staffId, fromGroupId: homeGroupId })
  }
  const endDrag = () => {
    setDragging(null)
    setDragOverGroupId(undefined)
  }
  const dragOverGroup = (e, groupId) => {
    if (!canReorg || !dragging || dragging.fromGroupId === groupId) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (dragOverGroupId !== groupId) setDragOverGroupId(groupId)
  }
  const dropOnGroup = (e, groupId) => {
    if (!canReorg || !dragging || dragging.fromGroupId === groupId) return
    e.preventDefault()
    onMoveStaff(dragging.staffId, dragging.fromGroupId, groupId)
    endDrag()
  }

  // One team can be pinned to a side panel — for scanning everyone else's board while
  // keeping an eye on one crew.
  const renderGroup = (group) => {
    const collapsed = collapsedGroups?.has(groupKey(group))
    const isDropTarget = canReorg && dragOverGroupId === group.id
    const rows = rowsFor.get(group.id) || []
    // Each team's block is washed with its own colour — the header a shade stronger than the
    // rows under it — so nine crews read as nine bands rather than one long list. Set as a
    // custom property rather than an inline background so the drop-target and cut states can
    // still override it from the stylesheet.
    const headTint = group.color ? tintOf(group.color, 0.8) : null
    const rowTint = group.color ? tintOf(group.color, 0.94) : null

    return (
      <Fragment key={groupKey(group)}>
        <tr
          className={`group-row ${isDropTarget ? 'drop-target' : ''} ${canCollapse ? 'clickable' : ''}`}
          style={headTint ? { '--team-tint': headTint } : undefined}
          onContextMenu={(e) => openTeamMenu(e, group.id)}
          onDragOver={(e) => dragOverGroup(e, group.id)}
          onDrop={(e) => dropOnGroup(e, group.id)}
        >
          <td
            colSpan={slotCount + 2}
            onClick={canCollapse ? () => onToggleCollapse(groupKey(group)) : undefined}
            title={canCollapse ? (collapsed ? 'Show this team' : 'Collapse this team') : undefined}
          >
            <span className="group-head">
              {canCollapse && (
                <button
                  className={`collapse-btn ${collapsed ? 'collapsed' : ''}`}
                  aria-label={collapsed ? 'Show this team' : 'Collapse this team'}
                  aria-expanded={!collapsed}
                  onClick={(e) => { e.stopPropagation(); onToggleCollapse(groupKey(group)) }}
                >
                  <Icon name="down" size={14} strokeWidth={2} />
                </button>
              )}
              <GroupHeaderContent group={group} />
              {collapsed && rows.length > 0 && (
                <span className="group-count">{rows.length}</span>
              )}
            </span>
          </td>
        </tr>

        {!collapsed && rows.map((staffId) => {
          const staff = staffById.get(staffId)
          if (!staff) return null
          // "Visiting" is measured against the crew they are on TODAY, not the template —
          // somebody moved here for the day belongs here today and is not a guest.
          const homeGroupId = crewToday(staffId)
          const visiting = board.mode === 'cat' && homeGroupId !== group.id
          // The third borrow marker, and the one the switch in Group setup was never wired
          // to. Entry.jsx had exactly this bug and was fixed; this call site was missed, so
          // turning the switch off silenced the chip on the job and left this one on the
          // name beside it.
          //
          // Never the unassigned pseudo-group either. Somebody on no crew who is booked
          // with one gets a row there and counts as visiting, so this used to read
          // "← Not on a team" — naming as their origin a place that is the absence of a
          // crew rather than a crew they could be borrowed from. Same guard, same reason,
          // as the one in Entry.jsx.
          const visitingFrom = visiting ? groupsById.get(homeGroupId) : null
          const homeGroup =
            showBorrowMarkers && visitingFrom?.kind !== 'unassigned' ? visitingFrom : null
          const isCut = cut?.staffId === staffId
          const isDragging = dragging?.staffId === staffId
          const off = offToday.get(staffId)
          const partOff = off ? null : offPartToday.get(staffId)

          return (
            <tr
              key={`${group.id}-${staffId}`}
              className={[
                'staff-row',
                isDragging ? 'dragging' : '',
                isCut ? 'staff-row-cut' : '',
                isDropTarget ? 'drop-target' : '',
                off ? 'staff-row-off' : '',
              ].filter(Boolean).join(' ')}
              style={rowTint ? { '--team-tint': rowTint } : undefined}
              onDragOver={(e) => dragOverGroup(e, group.id)}
              onDrop={(e) => dropOnGroup(e, group.id)}
              onContextMenu={(e) => openRowMenu(e, staffId, homeGroupId)}
            >
              {/* The name cell is the drag handle, not the whole row: a row-wide handle
                  fights the job cells, whose own job is to be clicked into and typed in. */}
              <td
                // Same as the week grid's: marked while the menu it opened is showing.
                className={`staff-cell ${
                  menu?.kind === 'row' && menu.staffId === staffId ? 'menu-open' : ''
                }`}
                draggable={canReorg}
                onDragStart={(e) => startDrag(e, staffId, homeGroupId)}
                onDragEnd={endDrag}
                title={canReorg ? 'Drag to move to another team' : undefined}
              >
                {canReorg && <Icon name="grip" size={13} strokeWidth={2.4} className="row-grip" />}
                {staff.name}
                {homeGroup && (
                  <span className="chip" style={{ marginLeft: 6 }}>
                    {homeGroup.color && (
                      <span className="group-swatch" style={{ background: homeGroup.color }} />
                    )}
                    ← {homeGroup.name}
                  </span>
                )}
              </td>
              {/* One banner across every job column rather than "Off" repeated in each:
                  being away is one fact about the day, not a job done three times. */}
              {off ? (
                <td className="off-cell off-hatch" colSpan={slotCount}>
                  <OffBadge t={off} onEdit={onEditTimeOff} />
                </td>
              ) : slots.map((slot) => {
                const key = `${group.id || 'none'}:${staffId}:${slot}`
                // A day cell is one slot and holds one job, so adding "another" means the
                // next empty slot along this row. Null when the row is full — there is no
                // column left to put it in, and offering a + that cannot land anywhere is
                // worse than not offering one.
                const nextFree = slots.find((s2) => !cellIndex.get(`${group.id || 'none'}:${staffId}:${s2}`))
                const entry = cellIndex.get(key)
                const lentOut = !entry && borrowedOut.get(`${group.id}:${staffId}:${slot}`)
                const editable = readOnly || lentOut
                  ? false
                  : entry
                    ? canEdit(entry)
                    : canCreateIn(group)
                const isEditing = editing === key

                return (
                  <td
                    key={slot}
                    onContextMenu={(e) =>
                      openCellMenu(e, { staffId, groupId: homeGroupId, slot, entry, editable, group })
                    }
                    onDragOver={(e) => {
                      if (!canDropHere(draggingEntry, e, editable, entry)) return
                      e.preventDefault()
                      e.dataTransfer.dropEffect = 'copy'
                      if (dropCell !== key) setDropCell(key)
                    }}
                    onDragLeave={(e) => {
                      // dragleave also fires on crossing into a CHILD of this cell, which
                      // would flicker the highlight off and on for the whole drag.
                      if (e.currentTarget.contains(e.relatedTarget)) return
                      setDropCell((c) => (c === key ? null : c))
                    }}
                    onDrop={(e) => {
                      if (!canDropHere(draggingEntry, e, editable, entry)) return
                      e.preventDefault()
                      onSave({
                        entry,
                        patch: {
                          label: draggingEntry.label,
                          jobTypeId: draggingEntry.jobTypeId,
                          ...(byPerson ? { catTeamId: draggingEntry.catTeamId ?? null } : {}),
                        },
                        staffId,
                        slot,
                        date,
                        group,
                      })
                      setDraggingEntry(null)
                      setDropCell(null)
                    }}
                  >
                    <div
                      // Published so a cell can be found from outside the grid — the notes
                      // drawer reveals the cell a note was written against, and needs a way
                      // to reach it that does not depend on row and column indices. It is
                      // also what makes the cell a stop for the arrow keys (lib/useGridKeys.js).
                      data-cell-key={key}
                      // Focusable by click and by the arrows, never by Tab — the table is the
                      // one tab stop, and it hands focus in.
                      tabIndex={readOnly ? undefined : -1}
                      // `menu-open` marks the cell a right-click menu belongs to, the same
                      // way `editing` marks the one the editor is over. The menu floats free
                      // of the table, so without it a board of identical cells gave no clue
                      // which one the Copy or Paste about to be clicked would act on.
                      className={`cell ${!entry && !lentOut ? 'empty-cell' : ''} ${
                        // An empty cell on a stat day is labelled rather than blank. It stays
                        // as bookable as any other — see .stat-empty in styles.css.
                        !entry && !lentOut && day?.holiday?.observed ? 'stat-empty' : ''
                      } ${
                        editable ? '' : 'readonly'
                      } ${isEditing ? 'editing' : ''} ${dropCell === key ? 'drop-target' : ''} ${
                        menu?.kind === 'cell' && menu.staffId === staffId && menu.slot === slot
                          ? 'menu-open'
                          : ''
                      } ${revealedKey === key ? 'revealed' : ''}`}
                      title={entry?.internalNote || undefined}
                      // The editor is a popover anchored to this cell rather than a form
                      // rendered inside it, so opening one does not resize the row and shift
                      // every other cell out from under the pointer.
                      onClick={(e) => {
                        if (!editable || isEditing) return
                        openEditor(key, e.currentTarget.getBoundingClientRect(), {
                          entry, staffId, slot, group, staffJobTypeIds: staff.jobTypeIds, staffName: staff.name,
                        })
                      }}
                      // Draggable regardless of whether THIS cell is editable — copying reads
                      // the job, it does not touch it, so the same permission that lets the
                      // day view's own "Copy entry" menu item show on a read-only cell applies
                      // here too. Only the drop target's own editable gates anything.
                      draggable={Boolean(entry) && !readOnly}
                      onDragStart={(e) => {
                        if (!entry) return
                        e.dataTransfer.setData('text/plain', entry.label)
                        e.dataTransfer.effectAllowed = 'copy'
                        // The id rides along so a cell can refuse to be its own drop target.
                        setDraggingEntry({
                          id: entry.id,
                          label: entry.label,
                          jobTypeId: entry.jobTypeId,
                          catTeamId: entry.crewGroupId ?? entry.catTeamId ?? null,
                        })
                      }}
                      onDragEnd={() => { setDraggingEntry(null); setDropCell(null) }}
                    >
                      {/* In the first job cell, where the week grid puts it too, and never on
                          the name: the hours are a fact about the day’s work, and the cells are
                          where the day’s work is read. Above whatever is booked, not instead
                          of it — the jobs either side of the appointment are real. */}
                      {slot === 1 && partOff && <OffPartialTile t={partOff} date={date} onEdit={onEditTimeOff} />}
                      {entry ? (
                        <Entry
                          entry={entry}
                          board={board}
                          jobTypes={jobTypes}
                          groupsById={groupsById}
                          showBorrow={showBorrowMarkers}
                          crewed={byPerson}
                          crewStyle={crewStyleFor(entry, crewsById)}
                          crewName={crewsById.get(entry.crewGroupId)?.name}
                        />
                      ) : lentOut && showBorrowMarkers ? (
                        <span className="out-with">
                          out with {groupsById.get(lentOut.catTeamId)?.name || 'another team'}
                        </span>
                      ) : null}
                      {/* Same affordance as the week grid's, on the cell that already has a
                          job. It books into the next free slot rather than this one, which is
                          taken — so it adds a job to the person's day rather than replacing
                          the one you clicked beside. */}
                      {entry && nextFree && !readOnly && canCreateIn(group) && (
                        <button
                          type="button"
                          className="cell-add"
                          title="Add another job"
                          aria-label="Add another job"
                          onClick={(ev) => {
                            ev.stopPropagation()
                            openEditor(
                              `${group.id || 'none'}:${staffId}:${nextFree}`,
                              ev.currentTarget.getBoundingClientRect(),
                              {
                                entry: null,
                                staffId,
                                slot: nextFree,
                                group,
                                staffJobTypeIds: staff.jobTypeIds,
                                staffName: staff.name,
                              },
                            )
                          }}
                        >
                          <Icon name="plus" size={13} strokeWidth={2.4} />
                        </button>
                      )}
                    </div>
                  </td>
                )
              })}
              <td className="col-add" />
            </tr>
          )
        })}
      </Fragment>
    )
  }

  // A plain element, not a nested component: a component declared inside render is a new
  // type on every pass, so React would tear down and rebuild the header each time. The same
  // element object can safely be rendered in both tables.
  const tableHead = (
    <thead>
      <tr>
        <th className="col-staff">Staff</th>
        {slots.map((s) => <th key={s}>Job {s}</th>)}
        <th className="col-add">
          {!readOnly && onAddColumn && (
            <button className="btn sm no-print" onClick={onAddColumn}
              title="Add a job column for this day">
              +
            </button>
          )}
        </th>
      </tr>
    </thead>
  )

  // Resolved in stack order, dropping a key that no longer matches any group (a team retired
  // since this was saved) rather than leaving a gap in the stack.
  const leftGroups = pins.left.map((k) => board.groups.find((g) => groupKey(g) === k)).filter(Boolean)
  const rightGroups = pins.right.map((k) => board.groups.find((g) => groupKey(g) === k)).filter(Boolean)
  const pinnedKeys = new Set([...pins.left, ...pins.right])
  const tableGroups = pinnedKeys.size ? board.groups.filter((g) => !pinnedKeys.has(groupKey(g))) : board.groups
  // Dragging either divider resizes that SIDE's column — shared by every team stacked in it,
  // since they are one column, not one panel each. Which way the pointer has to travel to
  // make it wider depends on which edge it's on, hence the sign.
  const startResize = (edge) => (e) => {
    e.preventDefault()
    const startX = e.clientX
    const startWidth = sideWidths[edge]
    const sign = edge === 'left' ? 1 : -1
    const onMove = (ev) =>
      onSetSideWidths?.({
        ...sideWidths,
        [edge]: Math.max(260, Math.min(720, startWidth + sign * (ev.clientX - startX))),
      })
    const onUp = () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
      document.body.classList.remove('col-resizing')
    }
    // A body class for the duration: without it the pointer flickers back to a text caret
    // the moment it leaves the 7px divider, and dragging selects the board's text.
    document.body.classList.add('col-resizing')
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
  }

  // One stack per side: every pinned team in it shares the side's width and scrolls as one
  // unit, which is the point of "they stack" — a resizer per team would mean resizing one
  // team's column independently shoves the others sideways under it, and a sticky offset per
  // team would mean the second one overlaps the first the moment both are tall enough.
  const renderStack = (groups, edge) =>
    groups.length > 0 && (
      <div className="board-side-stack" style={{ flexBasis: sideWidths[edge] }}>
        {groups.map((g) => (
          <aside key={groupKey(g)} className="board-wrap board-side">
            <table className={`board ${byPerson ? 'by-people' : ''}`} {...gridKeys}>
              {tableHead}
              <tbody>{renderGroup(g)}</tbody>
            </table>
          </aside>
        ))}
      </div>
    )
  const resizerFor = (edge, groups) =>
    groups.length > 0 && (
      <div
        className="board-resizer no-print"
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize the ${edge}-pinned teams`}
        onMouseDown={startResize(edge)}
        onDoubleClick={() => onSetSideWidths?.({ ...sideWidths, [edge]: 380 })}
        title="Drag to resize · double-click to reset"
      />
    )

  const menuGroup = menu?.kind === 'team' ? groupsById.get(menu.groupId) : null
  const menuGroupKey = menuGroup ? groupKey(menuGroup) : null
  const menuInLeft = menuGroupKey != null && pins.left.includes(menuGroupKey)
  const menuInRight = menuGroupKey != null && pins.right.includes(menuGroupKey)
  // Pinning to a side removes the team from the OTHER side first — one edit to move a team
  // across, not an unpin followed by a separate pin, and never in both stacks at once.
  const pinTo = (edge) => {
    const other = edge === 'left' ? 'right' : 'left'
    onSetPins?.({
      [edge]: [...pins[edge], menuGroupKey],
      [other]: pins[other].filter((k) => k !== menuGroupKey),
    })
  }
  const unpin = () =>
    onSetPins?.({
      left: pins.left.filter((k) => k !== menuGroupKey),
      right: pins.right.filter((k) => k !== menuGroupKey),
    })

  // Cut/paste-row is offered from the row menu and from a cell menu alike, so it is built
  // once rather than kept in step in two places.
  const rowActions = (staffId, homeGroupId) => (
    <>
      {canReorg && (
        <>
          <ContextMenuItem
            icon="cut"
            onClick={() => { onCutStaff(staffId, homeGroupId); setMenu(null) }}
          >
            Cut row
          </ContextMenuItem>
          {cut && (
            <ContextMenuItem
              icon="paste"
              disabled={cut.fromGroupId === homeGroupId}
              onClick={() => { onMoveStaff(cut.staffId, cut.fromGroupId, homeGroupId); setMenu(null) }}
            >
              Paste row
            </ContextMenuItem>
          )}
        </>
      )}
      {canTimeOff && (
        <>
          {canReorg && <ContextMenuSeparator />}
          <ContextMenuItem
            icon="calendar"
            onClick={() => { onAddTimeOff(staffId); setMenu(null) }}
          >
            Add time off
          </ContextMenuItem>
        </>
      )}
    </>
  )

  return (
    <div
      className={`board-layout ${leftGroups.length ? 'has-side side-left' : ''} ${rightGroups.length ? 'has-side side-right' : ''}`}
    >
      {renderStack(leftGroups, 'left')}
      {resizerFor('left', leftGroups)}

      <div className="board-wrap">
        <table className={`board ${byPerson ? 'by-people' : ''}`} {...gridKeys}>
          {tableHead}
          <tbody>{tableGroups.map((g) => renderGroup(g))}</tbody>
        </table>
      </div>

      {resizerFor('right', rightGroups)}
      {renderStack(rightGroups, 'right')}

      {editing && editCtx && (
        <CellEditor
          crews={byPerson ? crews : null}
          // Their standing crew, so a new job on a people board starts where it most
          // likely belongs. staff[].homeGroupId is the server's answer and survives the
          // reshape untouched — homeGroupOfStaff would say the synthetic band here.
          defaultCrewId={byPerson ? staffById.get(editCtx.staffId)?.homeGroupId || '' : null}
          entry={editCtx.entry}
          jobTypes={board.jobTypes}
          staffJobTypeIds={editCtx.staffJobTypeIds}
          staffName={editCtx.staffName}
          slot={editCtx.slot}
          dateLabel={formatShort(date)}
          labels={dayLabels}
          anchor={editCtx.anchor}
          onSave={(patch) =>
            onSave({
              entry: editCtx.entry,
              patch,
              staffId: editCtx.staffId,
              slot: editCtx.slot,
              date,
              group: editCtx.group,
            })
          }
          onDelete={() => onDelete(editCtx.entry)}
          onCancel={closeEditor}
        />
      )}

      {menu && (
        <ContextMenu x={menu.x} y={menu.y} onClose={() => setMenu(null)}>
          {menu.kind === 'row' && (
            <>
              <ContextMenuLabel>{staffById.get(menu.staffId)?.name}</ContextMenuLabel>
              {rowActions(menu.staffId, menu.groupId)}
            </>
          )}

          {menu.kind === 'cell' && (
            <>
              <ContextMenuLabel>
                {staffById.get(menu.staffId)?.name} · Job {menu.slot}
              </ContextMenuLabel>
              {menu.entry && (
                <ContextMenuItem
                  icon="copy"
                  onClick={() => {
                    onCopyEntry?.({
                      label: menu.entry.label,
                      jobTypeId: menu.entry.jobTypeId,
                      catTeamId: menu.entry.crewGroupId ?? menu.entry.catTeamId ?? null,
                    })
                    setMenu(null)
                  }}
                >
                  Copy entry
                </ContextMenuItem>
              )}
              {clip && (
                <ContextMenuItem
                  icon="paste"
                  disabled={!menu.editable}
                  onClick={() => {
                    onSave({
                      entry: menu.entry,
                      patch: {
                        label: clip.label,
                        jobTypeId: clip.jobTypeId,
                        // Grouped by crew the band decides the crew, so a paste into another
                        // band must take that band's. Grouped by person there is one band and
                        // no such answer, so the copy carries its own crew — and its colour
                        // with it, which is the whole of what the cell looks like there.
                        ...(byPerson ? { catTeamId: clip.catTeamId ?? null } : {}),
                      },
                      staffId: menu.staffId,
                      slot: menu.slot,
                      date,
                      group: menu.group,
                    })
                    // The copy stays held, so the same job can go on the next day and the
                    // next. The strip above the board says what is held and Escape lets it go.
                    setMenu(null)
                  }}
                >
                  Paste entry
                </ContextMenuItem>
              )}
              {menu.entry && menu.editable && (
                <ContextMenuItem
                  icon="trash"
                  onClick={() => { onDelete(menu.entry); setMenu(null) }}
                >
                  Clear cell
                </ContextMenuItem>
              )}
              {onCopyDay && board.assignments.some((a) => a.staffId === menu.staffId && a.date === date) && (
                <ContextMenuItem
                  icon="calendarRange"
                  onClick={() => { onCopyDay(menu.staffId, date); setMenu(null) }}
                >
                  Copy this day to…
                </ContextMenuItem>
              )}
              {canReorg && (menu.entry || clip) && <ContextMenuSeparator />}
              {rowActions(menu.staffId, menu.groupId)}
            </>
          )}

          {menu.kind === 'team' && (
            <>
              <ContextMenuLabel>{menuGroup?.name || 'Unassigned staff'}</ContextMenuLabel>
              {cut && canReorg && (
                <ContextMenuItem
                  icon="paste"
                  disabled={cut.fromGroupId === menu.groupId}
                  onClick={() => { onMoveStaff(cut.staffId, cut.fromGroupId, menu.groupId); setMenu(null) }}
                >
                  Paste row
                </ContextMenuItem>
              )}
              {canPin && (
                <>
                  {cut && canReorg && <ContextMenuSeparator />}
                  {(menuInLeft || menuInRight) && (
                    <ContextMenuItem icon="back" onClick={() => { unpin(); setMenu(null) }}>
                      Back to table
                    </ContextMenuItem>
                  )}
                  {/* Both sides stay offered even from a side already pinned the other way —
                      picking one moves the team across rather than requiring Back to table
                      first, since pinTo already un-pins it from wherever it was. Only the
                      side it is CURRENTLY on drops off, as the one move that would do nothing. */}
                  {!menuInLeft && (
                    <ContextMenuItem icon="panelLeft" onClick={() => { pinTo('left'); setMenu(null) }}>
                      {menuInRight ? 'Move to left panel' : 'Pin to left'}
                    </ContextMenuItem>
                  )}
                  {!menuInRight && (
                    <ContextMenuItem icon="panelRight" onClick={() => { pinTo('right'); setMenu(null) }}>
                      {menuInLeft ? 'Move to right panel' : 'Pin to right'}
                    </ContextMenuItem>
                  )}
                </>
              )}
            </>
          )}
        </ContextMenu>
      )}
    </div>
  )
}

/**
 * WEEK VIEW — rows are people, columns are the working days, entries stacked per cell.
 * The "what does this week look like" glance rather than the editing surface. Team
 * reorganisation (drag/cut/paste, collapse) lives in the day view only — but time off does
 * not, because a week is exactly the view somebody is looking at when they realise a person
 * is away for three days of it.
 *
 * Copying and editing a job are the pieces of the day view's editing vocabulary that DO live
 * here: right-click a job for Copy, right-click a cell for Paste, left-click a job to edit
 * it, and Ctrl-drag a job onto another cell to copy it there directly — all four are the
 * same actions the day view offers, just reached the way this view's cells are shaped: a
 * week cell holds every job for that day stacked together rather than one job per column, so
 * there is no slot COLUMN to name the target the way the day view's own gestures do. Pasting
 * onto a specific job overwrites it; pasting onto the cell's empty space finds that person's
 * next open slot on that day.
 */
export function WeekGrid({
  board,
  crews = null,
  onPickDay,
  onAddTimeOff,
  onEditTimeOff = null,
  canCreateIn,
  canEdit,
  onSave,
  onDelete,
  clip = null, // { label, jobTypeId, catTeamId } | null — a copied job, for paste elsewhere
  onCopyEntry,
  onCopyDay = null,
  readOnly = false,
  // Ten to fourteen columns will not fit a laptop at a readable width. `wide` lets the table
  // size to its content and scroll sideways instead of squeezing every cell to nothing.
  wide = false,
  // Rows drawn after the crews, inside this table so their day columns are these columns.
  // The fleet band uses it (components/FleetBand.jsx); the grid itself knows nothing of what
  // they are.
  afterGroups = null,
  // Folding a crew away. The same shape the day grid takes, and the same state behind it, so
  // a band shut on one view is shut on the other.
  collapsedGroups = null,
  onToggleCollapse = null,
  revealedKey = null,
}) {
  const { tenant, modeName, enabledModes } = useAuth()
  // The lip marks the days that run on the OTHER mode. With only one mode enabled there is
  // no other mode, so it is a label on every column saying the only thing it could say.
  const showModeLip = enabledModes().length > 1
  const jobTypes = new Map(board.jobTypes.map((j) => [j.id, j]))
  const groupsById = new Map(board.groups.map((g) => [g.id, g]))
  const staffById = new Map(board.staff.map((s) => [s.id, s]))
  const days = board.days
  /**
   * Which column starts the second week, on the fortnight view.
   *
   * Worked out from the dates rather than by counting to five: `days` holds only the tenant's
   * working days, so a company that works Saturdays has six columns in a week and one that
   * takes Fridays off has four. Comparing each day's week start to the first day's is true
   * for all of them.
   */
  const weekStart = tenant?.weekStart || 1
  const firstWeek = days.length ? startOfWeek(days[0].date, weekStart) : null
  // The FIRST day of the second week, not every day in it — the rule marks the boundary, and
  // testing "is this day in week two" put a line down all seven of its columns.
  const splitDate =
    (wide && firstWeek
      ? days.find((d) => startOfWeek(d.date, weekStart) !== firstWeek)?.date
      : null) || null
  const startsSecondWeek = (d) => d.date === splitDate
  // Column index of the boundary within `days`; -1 on a view that has no split.
  const splitIndex = splitDate ? days.findIndex((d) => d.date === splitDate) : -1
  const [menu, setMenu] = useState(null) // { x, y, kind:'row'|'cell', ... } | null
  const [editCtx, setEditCtx] = useState(null) // { entry, staffId, date, group, anchor } | null
  const [draggingEntry, setDraggingEntry] = useState(null) // { id, label, jobTypeId } | null
  const [dropCell, setDropCell] = useState(null) // `${staffId}:${date}` a copy-drag is over
  // Which merged bar is under the pointer, if any. Hovering splits it back into its days,
  // and that has to be known by every cell in the run at once — a td cannot see its
  // siblings' :hover, so the row reports it and the cells read it.
  const [hoverRun, setHoverRun] = useState(null)
  const tableRef = useRef(null)

  /**
   * Stretch each bar's name across the bar it belongs to.
   *
   * Measured rather than calculated: the day columns are NOT a uniform width — they size to
   * their contents, so a week can run 112, 141, 172, 142 — and "the run's width is one column
   * times the number of days" put the name 61px off centre on a four-day bar. The first and
   * last cells of the run know the truth between them.
   *
   * After layout and on any resize, since dragging the side panels or the window changes
   * every column width without changing a thing about the board's data.
   */
  useLayoutEffect(() => {
    const table = tableRef.current
    if (!table) return
    const place = () => {
      for (const label of table.querySelectorAll('.run-label')) {
        const first = label.closest('td')
        let last = first
        for (let i = 1; i < Number(label.dataset.runLen || 1); i++) last = last.nextElementSibling || last
        label.style.width = `${last.getBoundingClientRect().right - first.getBoundingClientRect().left}px`
      }
    }
    place()
    const ro = new ResizeObserver(place)
    ro.observe(table)
    return () => ro.disconnect()
  })
  // Grouped by person there is one band and the crew rides on each job, so it arrives
  // alongside the board for colour and for the editor's picker.
  const byPerson = isPeopleBoard(board)
  const crewsById = new Map((crews || []).map((c) => [c.id, c]))
  const canTimeOff = !readOnly && Boolean(onAddTimeOff)

  const byCell = new Map()
  for (const a of board.assignments) {
    const key = `${a.staffId}:${a.date}`
    if (!byCell.has(key)) byCell.set(key, [])
    byCell.get(key).push(a)
  }

  // Away on a given day, keyed by person, so a week row can mark each column independently.
  // offOn is the WHOLE day — the one that empties the cell and ends a job run. A window
  // within the day is partOffOn, drawn above whatever is booked, and it must not blank the
  // cell: the jobs either side of the appointment are real and still need doing.
  const overlaps = (t, staffId, d) => t.staffId === staffId && t.from <= d && d <= t.to
  const offOn = (staffId, d) =>
    (board.timeOff || []).find((t) => overlaps(t, staffId, d) && !isPartialOn(t, d))
  const partOffOn = (staffId, d) =>
    (board.timeOff || []).find((t) => overlaps(t, staffId, d) && isPartialOn(t, d))

  // Same rule as the day view: a borrowed-in person gets a row in the team they are
  // working for, or their week is indexed to a group that never draws it.
  const rowsFor = new Map(board.groups.map((g) => [g.id, [...g.staffIds]]))
  for (const a of board.assignments) {
    const rows = rowsFor.get(a.renderGroupId)
    if (rows && !rows.includes(a.staffId)) rows.push(a.staffId)
  }

  /** The lowest job number nobody in this cell is already sitting in. Fills a gap left by a
   *  deleted job before it ever extends past the day's usual column count. */
  const firstOpenSlot = (entries) => {
    const taken = new Set(entries.map((e) => e.slot))
    let slot = 1
    while (taken.has(slot)) slot++
    return slot
  }

  const closeEditor = () => {
    // The cell the editor was over gets focus back, so the arrow keys resume from it.
    focusCell(editCtx?.cellKey)
    setEditCtx(null)
  }
  const gridKeys = useGridKeys(!readOnly)
  /**
   * `slot` is carried on the context rather than read off the entry, because there may not
   * be an entry: clicking a week cell's empty space books a NEW job, and the slot it lands
   * in is that person's next free one on the day — the same number the paste and the
   * copy-drag pick for the same cell.
   */
  const openEditor = (entry, anchor, ctx) =>
    setEditCtx({ entry, anchor, ...ctx, slot: ctx.slot ?? entry?.slot ?? 1 })

  /** entry is the specific job right-clicked, or null for the cell's empty background. */
  const openCellMenu = (e, ctx) => {
    if (readOnly) return
    // Nothing to copy, nothing to paste AND nowhere to book somebody off — only then is the
    // menu empty and the native one worth more. Booking time off needs no job in the cell,
    // which is the whole point: a blank Tuesday is exactly where you notice somebody is away.
    if (!ctx.entry && !clip && !canTimeOff) return
    e.preventDefault()
    e.stopPropagation()
    setMenu({ x: e.clientX, y: e.clientY, kind: 'cell', ...ctx })
  }

  const pasteFromClip = (targetEntry, { staffId, date, group, entries }) => {
    if (!clip || !onSave) return
    onSave({
      entry: targetEntry || null,
      patch: {
                        label: clip.label,
                        jobTypeId: clip.jobTypeId,
                        // Grouped by crew the band decides the crew, so a paste into another
                        // band must take that band's. Grouped by person there is one band and
                        // no such answer, so the copy carries its own crew — and its colour
                        // with it, which is the whole of what the cell looks like there.
                        ...(byPerson ? { catTeamId: clip.catTeamId ?? null } : {}),
                      },
      staffId,
      slot: targetEntry ? targetEntry.slot : firstOpenSlot(entries),
      date,
      group,
    })
    // The copy stays held, as on the day view, so one job can be pasted along a week.
  }

  return (
    <div className={`board-wrap ${wide ? 'wide' : ''}`}>
      <table ref={tableRef} className={`board ${byPerson ? 'by-people' : ''}`} {...gridKeys}>
        <thead>
          <tr>
            <th className="col-staff">Staff</th>
            {days.map((d) => {
              const clickable = !readOnly && Boolean(onPickDay)
              // Marked, and not marked: the second gets a class and no visible treatment,
              // so a tenant stylesheet or a later design can say something about it without
              // this file changing.
              const stat = d.holiday?.observed ? 'holiday' : d.holiday ? 'holiday-dismissed' : ''
              return (
                <th
                  key={d.date}
                  className={`week-day-head ${startsSecondWeek(d) ? 'week-split' : ''} ${stat}`}
                >
                  {showModeLip && d.mode === 'cat' && <span className="week-day-lip">{modeName('cat')}</span>}
                  {/* Both lips together on a CAT stat day. They are short, and dropping
                      either would hide something the scheduler needs. */}
                  {d.holiday?.observed && <span className="week-day-lip stat">STAT</span>}
                  {clickable ? (
                    <button
                      className="week-day-btn"
                      onClick={() => onPickDay(d.date)}
                      title={`Open ${weekdayName(d.date)} ${formatShort(d.date)}${
                        d.holiday?.observed ? ` · ${d.holiday.name}` : ''
                      }`}
                    >
                      <span className="week-day-name">{weekdayName(d.date)}</span>
                      <span className="week-day-date">{formatShort(d.date)}</span>
                    </button>
                  ) : (
                    <span className="week-day-btn">
                      <span className="week-day-name">{weekdayName(d.date)}</span>
                      <span className="week-day-date">{formatShort(d.date)}</span>
                    </span>
                  )}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {board.groups.map((group, gi) => {
            const rows = rowsFor.get(group.id) || []
            // Never on a people board: one band holds everybody, so folding it would hide the
            // whole schedule behind a single heading.
            const canFold = Boolean(onToggleCollapse) && !byPerson
            const shut = canFold && Boolean(collapsedGroups?.has(groupKey(group)))
            return (
            <Fragment key={group.id || `unassigned-${gi}`}>
              <GroupHeader
                group={group}
                colSpan={days.length + 1}
                splitIndex={splitIndex}
                collapsed={shut}
                count={rows.length}
                onToggle={canFold ? () => onToggleCollapse(groupKey(group)) : null}
              />
              {(shut ? [] : rows).map((staffId) => {
                const staff = staffById.get(staffId)
                if (!staff) return null
                const canCreateHere = !readOnly && Boolean(canCreateIn?.(group))
                // Grouped by crew as well as by person. A person can have a row under two
                // crews, but each row is asked only for that crew's days, so a day spent with
                // the other crew comes back empty here and ends the run — a bar never spans a
                // day this row is not the answer for.
                const runs = sameJobRuns(
                  days,
                  (date) =>
                    offOn(staffId, date)
                      ? []
                      : (byCell.get(`${staffId}:${date}`) || []).filter((a) =>
                          board.mode === 'cat' ? a.renderGroupId === group.id : true,
                        ),
                  `${group.id}-${staffId}`,
                )
                return (
                  <tr
                    key={`${group.id}-${staffId}`}
                    className="staff-row"
                    style={group.color ? { '--team-tint': tintOf(group.color, 0.94) } : undefined}
                    onMouseLeave={() => setHoverRun((k) => (k && k.startsWith(`${group.id}-${staffId}:`) ? null : k))}
                  >
                    {/* Right-click for time off lives on the name cell only — it used to be
                        the whole row, which meant it also fired on a job cell that a
                        right-click is now needed for copy/paste. */}
                    <td
                      // Marked while its own menu is open, the way a cell is while its editor
                      // is. Grouped by person the name is the only thing identifying a row,
                      // and a menu floating over a board of identical rows left nothing
                      // saying which one it had been opened from.
                      className={`staff-cell ${
                        menu?.kind === 'row' && menu.staffId === staffId ? 'menu-open' : ''
                      }`}
                      onContextMenu={(e) => {
                        if (!canTimeOff) return
                        e.preventDefault()
                        setMenu({ x: e.clientX, y: e.clientY, kind: 'row', staffId })
                      }}
                    >
                      {staff.name}
                    </td>
                    {days.map((d) => {
                      const off = offOn(staffId, d.date)
                      const partOff = off ? null : partOffOn(staffId, d.date)
                      const dayEntries = byCell.get(`${staffId}:${d.date}`) || []
                      const entries = dayEntries
                        .filter((a) => (board.mode === 'cat' ? a.renderGroupId === group.id : true))
                        .sort((a, b) => a.slot - b.slot)
                      // Booked that day, but with a different crew. Somebody working for two
                      // crews across a fortnight has a row under each, and on the days they
                      // are with the other one this cell is empty — which reads as free, and
                      // is the cell somebody double-books from. Hatching it says "already
                      // spoken for" without pretending to say where; the crew they are
                      // actually with is one row away.
                      //
                      // Group mode only. Normal mode draws a person's whole day in every row
                      // they appear in, so there is no empty cell to mark and the hatch would
                      // land on days that are genuinely free.
                      // Not under "Not on a team": that band is where people with no crew
                      // sit, so "out with another crew" is not a thing they can be — they
                      // are simply working, and the job is drawn under the crew doing it.
                      const elsewhere =
                        board.mode === 'cat' &&
                        group.kind !== 'unassigned' &&
                        !off &&
                        entries.length === 0 &&
                        dayEntries.length > 0
                      const cellCtx = { staffId, date: d.date, group, entries }
                      // The group is part of the key for the same reason it is in the day
                      // grid's: a borrowed person has a row under two crews on the same date,
                      // and keyed on staff and date alone both cells would light up together.
                      const cellKey = `${group.id || 'none'}:${staffId}:${d.date}`
                      // Where this cell sits in a same-job run, and whether the pointer is
                      // currently holding that run open. Merged, the run draws as one bar and
                      // only its first day says the name; hovered, every day is itself again.
                      const run = runs?.get(d.date) || null
                      const merged = Boolean(run) && hoverRun !== run.key
                      const runClass = merged
                        ? `run-merged ${run.first ? 'run-first' : ''} ${run.last ? 'run-last' : ''}`
                        : ''
                      return (
                        <td
                          key={d.date}
                          data-cell-key={cellKey}
                          // A stop for the arrow keys; Tab lands on the table, not here.
                          tabIndex={readOnly ? undefined : -1}
                          onMouseEnter={run ? () => setHoverRun(run.key) : undefined}
                          className={`week-cell ${
                            // Nothing in it, and something could be — the hover + is drawn on
                            // that alone. Not on a day somebody is away or out with another
                            // crew, where the save would refuse, and not where this person
                            // cannot create: an affordance for an action that will be turned
                            // down is worse than none.
                            entries.length === 0 && canCreateHere && !off && !elsewhere ? 'empty-cell' : ''
                          } ${off ? 'off-cell off-hatch' : ''} ${
                            elsewhere ? 'elsewhere-cell' : ''
                          } ${dropCell === cellKey ? 'drop-target' : ''} ${
                            editCtx?.cellKey === cellKey ? 'editing' : ''
                          } ${startsSecondWeek(d) ? 'week-split' : ''} ${
                            d.holiday?.observed ? 'holiday' : ''
                          } ${
                            d.holiday?.observed && entries.length === 0 && !off && !elsewhere
                              ? 'stat-empty'
                              : ''
                          } ${runClass} ${
                            menu?.kind === 'cell' && menu.staffId === staffId && menu.date === d.date
                              ? 'menu-open'
                              : ''
                          } ${revealedKey === cellKey ? 'revealed' : ''}`}
                          title={elsewhere ? 'Out with another crew this day' : undefined}
                          // The cell's own background, not the jobs sitting on it — a click
                          // on one of those means that job and opens it for editing. This
                          // opens the same CellEditor the day view does, given no entry, so
                          // booking work from the week reads exactly like booking it from the
                          // day. Refused on the days the day view would refuse too: somebody
                          // off, somebody already out with another crew, or no permission to
                          // create on this crew.
                          //
                          // Asked as "not on a job", not as "on the td itself". The stack
                          // that divides this cell between its jobs fills the whole of it, so
                          // an identity test against the cell matched nothing once that
                          // existed and no click anywhere in a week cell opened the editor.
                          onClick={(ev) => {
                            if (ev.target.closest('.week-entry')) return
                            if (off || elsewhere || !canCreateHere) return
                            openEditor(null, ev.currentTarget.getBoundingClientRect(), {
                              staffId,
                              date: d.date,
                              group,
                              cellKey,
                              slot: firstOpenSlot(entries),
                              staffJobTypeIds: staff.jobTypeIds,
                            })
                          }}
                          onContextMenu={(e) => {
                            // No "add a job here" on a day the person is already out with
                            // another crew — the menu's whole contents would be actions the
                            // save would have to refuse.
                            if (elsewhere) return
                            openCellMenu(e, { ...cellCtx, entry: null })
                          }}
                          onDragOver={(e) => {
                            if (!weekCanDrop(draggingEntry, e, off || elsewhere, canCreateHere)) return
                            e.preventDefault()
                            e.dataTransfer.dropEffect = 'copy'
                            if (dropCell !== cellKey) setDropCell(cellKey)
                          }}
                          onDragLeave={(e) => {
                            if (e.currentTarget.contains(e.relatedTarget)) return
                            setDropCell((c) => (c === cellKey ? null : c))
                          }}
                          onDrop={(e) => {
                            if (!weekCanDrop(draggingEntry, e, off || elsewhere, canCreateHere)) return
                            e.preventDefault()
                            const targetId = e.target.closest('.week-entry')?.dataset.entryId
                            const targetEntry = targetId ? entries.find((x) => x.id === targetId) : null
                            setDraggingEntry(null)
                            setDropCell(null)
                            // Onto the very entry being dragged: nothing to write.
                            if (targetEntry && targetEntry.id === draggingEntry.id) return
                            onSave({
                              entry: targetEntry || null,
                              patch: {
                          label: draggingEntry.label,
                          jobTypeId: draggingEntry.jobTypeId,
                          ...(byPerson ? { catTeamId: draggingEntry.catTeamId ?? null } : {}),
                        },
                              staffId,
                              slot: targetEntry ? targetEntry.slot : firstOpenSlot(entries),
                              date: d.date,
                              group,
                            })
                          }}
                        >
                          {off ? (
                            <OffBadge t={off} onEdit={onEditTimeOff} />
                          ) : (
                            // A stack that fills the cell, so one job occupies the whole of
                            // it and three share it in thirds. Without a wrapper the entries
                            // are loose children of a table cell and can only be as tall as
                            // their own text, which left most of a cell looking empty and
                            // unbooked when it was neither.
                            <div className="week-cell-inner">
                            <div className="week-cell-stack">
                            {partOff && <OffPartialTile t={partOff} date={d.date} onEdit={onEditTimeOff} />}
                            {entries.map((e) => {
                              const jobType = jobTypes.get(e.jobTypeId)
                              const editableHere = !readOnly && Boolean(canEdit?.(e))
                              return (
                                <div
                                  key={e.id}
                                  data-entry-id={e.id}
                                  className={`week-entry tile ${editableHere ? 'editable' : ''} ${
                                    byPerson ? 'crewed' : ''
                                  } ${isBirthday(e.label) ? 'birthday-entry' : ''}`}
                                  // Every job is a filled tile: grouped by person in its crew's
                                  // colour, grouped by crew in its band's. One look for both
                                  // views, so a run split open by the pointer is made of the
                                  // same tiles as the rest of the row, and a bar is those
                                  // tiles drawn once.
                                  style={
                                    byPerson
                                      ? crewStyleFor(e, crewsById)
                                      : group.color
                                        ? { '--crew': group.color }
                                        : undefined
                                  }
                                  // An internal note displaces the how-to hint. Somebody who
                                  // wrote "call the customer before you go" needs it to
                                  // surface on hover; "click to edit" is a thing you learn
                                  // once and then never read again.
                                  title={
                                    [
                                      byPerson ? crewsById.get(e.crewGroupId)?.name : null,
                                      e.internalNote,
                                    ]
                                      .filter(Boolean)
                                      .join('\n') ||
                                    (editableHere ? 'Click to edit · right-click to copy' : undefined)
                                  }
                                  draggable={!readOnly}
                                  onDragStart={(ev) => {
                                    ev.dataTransfer.setData('text/plain', e.label)
                                    ev.dataTransfer.effectAllowed = 'copy'
                                    // The id rides along so a drop onto this very entry can
                                    // be recognised and skipped.
                                    setDraggingEntry({
                                      id: e.id,
                                      label: e.label,
                                      jobTypeId: e.jobTypeId,
                                      catTeamId: e.crewGroupId ?? e.catTeamId ?? null,
                                    })
                                  }}
                                  onDragEnd={() => { setDraggingEntry(null); setDropCell(null) }}
                                  onContextMenu={(ev) => openCellMenu(ev, { ...cellCtx, entry: e })}
                                  onClick={(ev) => {
                                    if (!editableHere) return
                                    openEditor(e, ev.currentTarget.getBoundingClientRect(), {
                                      staffId, date: d.date, group, slot: e.slot, cellKey,
                                      staffJobTypeIds: staff.jobTypeIds,
                                    })
                                  }}
                                >
                                  {/* The words stay in the tile while merged, painted
                                      transparent (.run-merged in styles.css); the bar says
                                      them once, centred. They have to keep occupying their
                                      width: with them removed a column could shrink while
                                      merged and jump wider the moment the bar split open,
                                      when a label with no break in it ("Sobeys/Safeway")
                                      came back and pushed every column to its right along. */}
                                  {e.label}
                                  {!byPerson && jobType && ` ${jobType.name}`}
                                </div>
                              )
                            })}
                            </div>
                            {/* Only where there is already something to add ANOTHER to: an
                                empty cell is its own add button, and a + in every blank cell
                                of a fortnight board would be a hundred of them. Full height
                                and barely wider than the glyph, so it reads as an edge of
                                the cell rather than as one of the jobs in it. */}
                            {/* Centred on the bar, not on the cell that hosts it: spans the
                                run's full width and lets clicks through, so the day under
                                the pointer is still the day that opens. */}
                            {merged && run.first && entries[0] && (
                              <span
                                className="run-label"
                                data-run-len={run.len}
                                // Grouped by person the bar wears the job's crew colour;
                                // grouped by crew, the band's own.
                                style={
                                  byPerson
                                    ? crewStyleFor(entries[0], crewsById)
                                    : group.color
                                      ? { '--crew': group.color }
                                      : undefined
                                }
                              >
                                {entries[0].label}
                                {!byPerson &&
                                  jobTypes.get(entries[0].jobTypeId) &&
                                  ` ${jobTypes.get(entries[0].jobTypeId).name}`}
                              </span>
                            )}
                            {/* Rendered while merged too, and hidden by the stylesheet rather
                                than left out. The day columns size to their content, and this
                                button is the one thing that differs between a merged cell and
                                a split one: leaving it out made a bar's columns a little
                                narrower while merged, and the moment the bar split open the
                                buttons came back and shoved every column to the right along
                                — measured at 7px in one view and 19px in the other. */}
                            {entries.length > 0 && canCreateHere && (
                              <button
                                type="button"
                                className="cell-add"
                                title="Add another job"
                                aria-label="Add another job"
                                onClick={(ev) => {
                                  ev.stopPropagation()
                                  openEditor(null, ev.currentTarget.getBoundingClientRect(), {
                                    staffId,
                                    date: d.date,
                                    group,
                                    cellKey,
                                    slot: firstOpenSlot(entries),
                                    staffJobTypeIds: staff.jobTypeIds,
                                  })
                                }}
                              >
                                <Icon name="plus" size={13} strokeWidth={2.4} />
                              </button>
                            )}
                            </div>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                )
              })}
            </Fragment>
            )
          })}
          {afterGroups}
        </tbody>
      </table>

      {editCtx && (
        <CellEditor
          crews={byPerson ? crews : null}
          defaultCrewId={byPerson ? staffById.get(editCtx.staffId)?.homeGroupId || '' : null}
          entry={editCtx.entry}
          jobTypes={board.jobTypes}
          staffJobTypeIds={editCtx.staffJobTypeIds}
          staffName={staffById.get(editCtx.staffId)?.name}
          slot={editCtx.slot}
          dateLabel={formatShort(editCtx.date)}
          labels={[]}
          anchor={editCtx.anchor}
          // Closed on the RESULT, not on the click. This grid keeps its own editor state
          // rather than the caller's `editing`, so nothing was closing it: saving or deleting
          // left the popover sitting over a cell whose entry had just been changed or removed.
          // Awaiting the handler also means a save the person cancelled at the override
          // prompt, or one the server refused, correctly leaves the editor open.
          onSave={async (patch) => {
            const ok = await onSave({
              entry: editCtx.entry,
              patch,
              staffId: editCtx.staffId,
              slot: editCtx.slot,
              date: editCtx.date,
              group: editCtx.group,
            })
            if (ok) closeEditor()
          }}
          onDelete={async () => {
            if (await onDelete(editCtx.entry)) closeEditor()
          }}
          onCancel={closeEditor}
        />
      )}

      {menu && (
        <ContextMenu x={menu.x} y={menu.y} onClose={() => setMenu(null)}>
          {menu.kind === 'row' && (
            <>
              <ContextMenuLabel>{staffById.get(menu.staffId)?.name}</ContextMenuLabel>
              <ContextMenuItem
                icon="calendar"
                onClick={() => { onAddTimeOff(menu.staffId); setMenu(null) }}
              >
                Add time off
              </ContextMenuItem>
            </>
          )}

          {menu.kind === 'cell' && (
            <>
              <ContextMenuLabel>
                {staffById.get(menu.staffId)?.name} · {weekdayName(menu.date)}
              </ContextMenuLabel>
              {menu.entry && (
                <ContextMenuItem
                  icon="copy"
                  onClick={() => {
                    onCopyEntry?.({
                      label: menu.entry.label,
                      jobTypeId: menu.entry.jobTypeId,
                      catTeamId: menu.entry.crewGroupId ?? menu.entry.catTeamId ?? null,
                    })
                    setMenu(null)
                  }}
                >
                  Copy
                </ContextMenuItem>
              )}
              {clip && (
                <ContextMenuItem
                  icon="paste"
                  disabled={!canCreateIn?.(menu.group) && !(menu.entry && canEdit?.(menu.entry))}
                  onClick={() => {
                    pasteFromClip(menu.entry, menu)
                    setMenu(null)
                  }}
                >
                  Paste
                </ContextMenuItem>
              )}
              {onCopyDay && menu.entries?.length > 0 && (
                <ContextMenuItem
                  icon="calendarRange"
                  onClick={() => { onCopyDay(menu.staffId, menu.date); setMenu(null) }}
                >
                  Copy this day to…
                </ContextMenuItem>
              )}
              {/* Booking somebody off is reached from a job cell too, not only from their
                  name. Finding out that a person is away happens while looking at the day
                  they are away on, and the day view's cell menu has always offered it —
                  this is the same menu one view along. */}
              {canTimeOff && (
                <>
                  {(menu.entry || clip) && <ContextMenuSeparator />}
                  <ContextMenuItem
                    icon="calendar"
                    onClick={() => { onAddTimeOff(menu.staffId, menu.date); setMenu(null) }}
                  >
                    Add time off
                  </ContextMenuItem>
                </>
              )}
            </>
          )}
        </ContextMenu>
      )}
    </div>
  )
}
